/**
 * Secrets Inventory Page
 * AIDLC Administrative Frontend
 *
 * Tracks all secret keys, VAPIDs, connection strings, client secrets,
 * and their expiration dates. When a secret nears expiration, Todd
 * (Network Admin) is automatically alerted to rotate it.
 *
 * Personas: ops only
 */

import { useState, useEffect, useCallback } from 'react';
import { Card } from '../shared/Card';
import { Button } from '../shared/Button';
import { Modal } from '../shared/Modal';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import { AgentSelect } from '../shared/AgentSelect';
import { apiErrorMessage } from '../../utils/apiError';
import { useBridge } from '../layout/MainLayout';
import type { SecretEntry, SecretsSummary, SecretType, SecretSource, SecretStatus } from '../../types/secret';
import {
  getSecrets,
  getSecretsSummary,
  createSecret,
  updateSecret,
  deleteSecret,
  checkExpiry,
} from '../../services/secretService';

const statusConfig: Record<SecretStatus, { label: string; color: string; bg: string }> = {
  active: { label: 'Active', color: 'text-green-700', bg: 'bg-green-100' },
  expiring_soon: { label: 'Expiring Soon', color: 'text-yellow-700', bg: 'bg-yellow-100' },
  expired: { label: 'Expired', color: 'text-red-700', bg: 'bg-red-100' },
  not_configured: { label: 'Not Configured', color: 'text-gray-500', bg: 'bg-gray-100' },
  rotated: { label: 'Rotated', color: 'text-blue-700', bg: 'bg-blue-100' },
};

const typeLabels: Record<SecretType, string> = {
  client_secret: 'Client Secret',
  api_key: 'API Key',
  connection_string: 'Connection String',
  vapid_key: 'VAPID Key',
  certificate: 'Certificate',
  ssh_key: 'SSH Key',
  other: 'Other',
};

const sourceOptions: SecretSource[] = ['Entra ID', 'Azure Portal', 'Firebase Console', 'env', 'Key Vault', 'manual', 'other'];
const typeOptions: SecretType[] = ['client_secret', 'api_key', 'connection_string', 'vapid_key', 'certificate', 'ssh_key', 'other'];

function getDaysColor(days: number | null): string {
  if (days === null) return 'text-gray-400';
  if (days <= 0) return 'text-red-600 font-bold';
  if (days <= 30) return 'text-yellow-600 font-semibold';
  if (days <= 90) return 'text-blue-600';
  return 'text-green-600';
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

interface SecretFormData {
  name: string;
  type: SecretType;
  source: SecretSource;
  status: SecretStatus;
  maskedValue: string;
  configLocation: string;
  expiresAt: string;
  lastRotatedAt: string;
  rotationOwner: string;
  dependentServices: string;
  notes: string;
}

const emptyForm: SecretFormData = {
  name: '',
  type: 'client_secret',
  source: 'Entra ID',
  status: 'active',
  maskedValue: '',
  configLocation: '',
  expiresAt: '',
  lastRotatedAt: '',
  rotationOwner: 'todd',
  dependentServices: '',
  notes: '',
};

export function SecretsInventoryPage() {
  const { socket } = useBridge();
  const [secrets, setSecrets] = useState<SecretEntry[]>([]);
  const [summary, setSummary] = useState<SecretsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSecret, setEditingSecret] = useState<SecretEntry | null>(null);
  const [form, setForm] = useState<SecretFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<{ expiringSoon: string[]; expired: string[]; backlogCreated: string[] } | null>(null);

  const loadData = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const [secretsData, summaryData] = await Promise.all([
        getSecrets(params),
        getSecretsSummary(),
      ]);
      setSecrets(secretsData);
      setSummary(summaryData);
    } catch (err) {
      console.error('Failed to load secrets:', err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Socket.io listener for real-time expiry alerts
  useEffect(() => {
    if (!socket) return;
    const handleExpiring = () => {
      loadData();
    };
    socket.on('secret:expiring', handleExpiring);
    return () => {
      socket.off('secret:expiring', handleExpiring);
    };
  }, [socket, loadData]);

  const openAddModal = () => {
    setEditingSecret(null);
    setForm(emptyForm);
    setSaveError(null);
    setShowModal(true);
  };

  const openEditModal = (secret: SecretEntry) => {
    setEditingSecret(secret);
    setForm({
      name: secret.name,
      type: secret.type,
      source: secret.source,
      status: secret.status,
      maskedValue: secret.maskedValue,
      configLocation: secret.configLocation,
      expiresAt: secret.expiresAt ? secret.expiresAt.split('T')[0] : '',
      lastRotatedAt: secret.lastRotatedAt ? secret.lastRotatedAt.split('T')[0] : '',
      rotationOwner: secret.rotationOwner,
      dependentServices: secret.dependentServices.join(', '),
      notes: secret.notes,
    });
    setSaveError(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        source: form.source,
        status: form.status,
        maskedValue: form.maskedValue,
        configLocation: form.configLocation,
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
        lastRotatedAt: form.lastRotatedAt ? new Date(form.lastRotatedAt).toISOString() : null,
        rotationOwner: form.rotationOwner,
        dependentServices: form.dependentServices.split(',').map(s => s.trim()).filter(Boolean),
        notes: form.notes,
      };

      if (editingSecret) {
        await updateSecret(editingSecret.id, payload);
      } else {
        await createSecret(payload);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      console.error('Failed to save secret:', err);
      setSaveError(apiErrorMessage(err, 'The secret could not be saved. Check your connection and try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this secret entry? This cannot be undone.')) return;
    try {
      await deleteSecret(id);
      loadData();
    } catch (err) {
      console.error('Failed to delete secret:', err);
    }
  };

  const handleCheckExpiry = async () => {
    setChecking(true);
    setCheckResult(null);
    try {
      const result = await checkExpiry();
      setCheckResult(result);
      loadData();
    } catch (err) {
      console.error('Failed to check expiry:', err);
    } finally {
      setChecking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-ldaja-black">Secrets Inventory</h1>
          <p className="text-sm text-gray-500 mt-1">Track and manage all secret keys, API keys, and credentials</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={handleCheckExpiry} isLoading={checking}>
            Check Expiry
          </Button>
          <Button onClick={openAddModal}>
            + Add Secret
          </Button>
        </div>
      </div>

      {/* Expiry Check Result */}
      {checkResult && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
          <div className="font-medium text-blue-800 mb-1">Expiry Check Complete</div>
          <div className="text-blue-700 space-y-0.5">
            {checkResult.expired.length > 0 && (
              <div className="text-red-600">Expired: {checkResult.expired.join(', ')}</div>
            )}
            {checkResult.expiringSoon.length > 0 && (
              <div className="text-yellow-600">Expiring Soon: {checkResult.expiringSoon.join(', ')}</div>
            )}
            {checkResult.backlogCreated.length > 0 && (
              <div>Backlog items created: {checkResult.backlogCreated.join(', ')}</div>
            )}
            {checkResult.expired.length === 0 && checkResult.expiringSoon.length === 0 && (
              <div className="text-green-600">All secrets are healthy</div>
            )}
          </div>
          <button
            className="text-xs text-blue-500 hover:text-blue-700 mt-1"
            onClick={() => setCheckResult(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-ldaja-blue">{summary.total}</div>
            <div className="text-xs text-gray-500 mt-1">Total Secrets</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">{summary.active}</div>
            <div className="text-xs text-gray-500 mt-1">Active</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-600">{summary.expiringSoon}</div>
            <div className="text-xs text-gray-500 mt-1">Expiring Soon</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-600">{summary.expired + summary.notConfigured}</div>
            <div className="text-xs text-gray-500 mt-1">Expired / Not Configured</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          placeholder="Search secrets..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 max-w-sm px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue"
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue"
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="expiring_soon">Expiring Soon</option>
          <option value="expired">Expired</option>
          <option value="not_configured">Not Configured</option>
          <option value="rotated">Rotated</option>
        </select>
      </div>

      {/* Secrets Table */}
      <Card noPadding>
        {secrets.length === 0 ? (
          <div className="p-8">
            <EmptyState
              title="No secrets found"
              description={search || statusFilter ? 'Try adjusting your filters' : 'Add your first secret to start tracking'}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/50">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Source</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Expires</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Days Left</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Services</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {secrets.map(secret => {
                  const sc = statusConfig[secret.status] || statusConfig.active;
                  return (
                    <tr key={secret.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-ldaja-black">{secret.name}</div>
                        <div className="text-xs text-gray-400 mt-0.5 truncate max-w-[200px]" title={secret.configLocation}>
                          {secret.configLocation}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{typeLabels[secret.type] || secret.type}</td>
                      <td className="px-4 py-3 text-gray-600">{secret.source}</td>
                      <td className="px-4 py-3 text-gray-600">{formatDate(secret.expiresAt)}</td>
                      <td className="px-4 py-3">
                        <span className={getDaysColor(secret.daysRemaining)}>
                          {secret.daysRemaining !== null ? (
                            secret.daysRemaining <= 0 ? 'EXPIRED' : `${secret.daysRemaining}d`
                          ) : (
                            <span className="text-gray-300">N/A</span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${sc.bg} ${sc.color}`}>
                          {sc.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 max-w-[180px]">
                          {secret.dependentServices.slice(0, 2).map(svc => (
                            <span key={svc} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600 truncate max-w-[80px]" title={svc}>
                              {svc}
                            </span>
                          ))}
                          {secret.dependentServices.length > 2 && (
                            <span className="text-xs text-gray-400">+{secret.dependentServices.length - 2}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEditModal(secret)}
                            className="px-2 py-1 text-xs text-ldaja-blue hover:bg-blue-50 rounded transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(secret.id)}
                            className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingSecret ? 'Edit Secret' : 'Add Secret'}
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSave} isLoading={saving} disabled={!form.name.trim()}>
              {editingSecret ? 'Update' : 'Create'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {saveError && (
            <p role="alert" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {saveError}
            </p>
          )}
          <div className="grid grid-cols-2 gap-4">
            {/* Name */}
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Graph Client Secret"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue"
              />
            </div>

            {/* Type */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
              <select
                value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value as SecretType }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue"
              >
                {typeOptions.map(t => (
                  <option key={t} value={t}>{typeLabels[t]}</option>
                ))}
              </select>
            </div>

            {/* Source */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Source</label>
              <select
                value={form.source}
                onChange={e => setForm(f => ({ ...f, source: e.target.value as SecretSource }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue"
              >
                {sourceOptions.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <select
                value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value as SecretStatus }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue"
              >
                {Object.entries(statusConfig).map(([key, cfg]) => (
                  <option key={key} value={key}>{cfg.label}</option>
                ))}
              </select>
            </div>

            {/* Rotation Owner */}
            <div>
              <label htmlFor="secret-rotation-owner" className="block text-xs font-medium text-gray-700 mb-1">Rotation Owner</label>
              <AgentSelect
                id="secret-rotation-owner"
                value={form.rotationOwner}
                onChange={agentId => setForm(f => ({ ...f, rotationOwner: agentId }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue"
              />
            </div>

            {/* Masked Value */}
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Masked Value</label>
              <input
                type="text"
                value={form.maskedValue}
                onChange={e => setForm(f => ({ ...f, maskedValue: e.target.value }))}
                placeholder="e.g. ••••••••••••"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue font-mono"
              />
            </div>

            {/* Config Location */}
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Config Location</label>
              <input
                type="text"
                value={form.configLocation}
                onChange={e => setForm(f => ({ ...f, configLocation: e.target.value }))}
                placeholder="e.g. Entra ID > App Registrations > AIDLC"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue"
              />
            </div>

            {/* Expires At */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Expiration Date</label>
              <input
                type="date"
                value={form.expiresAt}
                onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue"
              />
            </div>

            {/* Last Rotated At */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Last Rotated</label>
              <input
                type="date"
                value={form.lastRotatedAt}
                onChange={e => setForm(f => ({ ...f, lastRotatedAt: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue"
              />
            </div>

            {/* Dependent Services */}
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Dependent Services (comma-separated)</label>
              <input
                type="text"
                value={form.dependentServices}
                onChange={e => setForm(f => ({ ...f, dependentServices: e.target.value }))}
                placeholder="e.g. Graph Email, MSAL Authentication"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue"
              />
            </div>

            {/* Notes */}
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={3}
                placeholder="Additional context or instructions for rotation..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue resize-none"
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
